import { useState } from "react";
import { Link } from "react-router";
import { Aviso, Botao, Cartao, Chip } from "../componentes/ui";
import { EditorDePainel } from "../componentes/paineis/EditorDePainel";
import { mensagemDoConsole } from "../nucleo/console";
import {
  useApagarPainel,
  useCatalogoDosPaineis,
  usePaineisDaEquipe,
} from "../nucleo/paineis";
import type { PainelNoConsole } from "../nucleo/paineis";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

/** Em edição: um painel existente, um novo (`"novo"`) ou nenhum (a lista). */
type Edicao = PainelNoConsole | "novo" | null;

/**
 * Onde a administração monta os painéis: escolhe widgets do catálogo fixo, define ordem e largura
 * e atribui o painel a papéis — que o recebem como tela inicial do console.
 */
export function PaineisDaEquipe() {
  useTitulo("Painéis");
  const sessao = useSessao();
  const voltarParaHome = useVoltarParaHome();
  const pode = podeNaSessao(sessao.data, "painel:gerenciar");
  const paineis = usePaineisDaEquipe(pode);
  const catalogo = useCatalogoDosPaineis(pode);
  const apagar = useApagarPainel();
  const [edicao, setEdicao] = useState<Edicao>(null);
  const [aApagar, setAApagar] = useState<string | null>(null);

  if (sessao.isPending) return <p role="status">Carregando…</p>;
  if (!pode) {
    return (
      <Aviso tom="atencao" data-testid="paineis-sem-permissao">
        Só a administração monta os painéis da equipe.
      </Aviso>
    );
  }

  if (edicao !== null && catalogo.data) {
    return (
      <EditorDePainel
        catalogo={catalogo.data}
        painel={edicao === "novo" ? undefined : edicao}
        aoSalvar={() => {
          setEdicao(null);
          voltarParaHome("Painel salvo.");
        }}
        aoCancelar={() => setEdicao(null)}
      />
    );
  }

  return (
    <>
      <h1 className="titulo-pagina">Painéis da equipe</h1>
      <p className="lead">
        Monte painéis com os números do atendimento e entregue cada um como tela inicial do console
        para os papéis que precisam dele. Todo número abre os chamados que o compõem.
      </p>
      <div className="mb-4">
        <Botao onClick={() => setEdicao("novo")} disabled={!catalogo.data}>
          Novo painel
        </Botao>
      </div>
      {paineis.isPending ? <p role="status">Carregando painéis…</p> : null}
      {paineis.isError ? <Aviso tom="erro">{mensagemDoConsole(paineis.error)}</Aviso> : null}
      {apagar.isError ? <Aviso tom="erro">{mensagemDoConsole(apagar.error)}</Aviso> : null}
      {paineis.data && paineis.data.length === 0 ? (
        <Aviso tom="neutro">Nenhum painel ainda. Os papéis seguem no console de sempre.</Aviso>
      ) : null}
      <div className="lista-de-itens">
        {(paineis.data ?? []).map((painel) => (
          <Cartao
            key={painel.id}
            titulo={painel.nome}
            data-testid={`painel-${painel.nome}`}
            acessorio={<Chip estado="neutro">{painel.widgets.length} widgets</Chip>}
          >
            {painel.descricao ? <p className="cartao__texto">{painel.descricao}</p> : null}
            <p className="nota-rodape mt-1">
              {painel.papeis.length > 0
                ? `Tela inicial de: ${painel.papeis.join(", ")}`
                : "Ainda não é a tela inicial de nenhum papel."}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Link className="botao botao--pequeno botao--fantasma" to={`/admin/paineis/${painel.id}`}>
                Visualizar
              </Link>
              <Botao
                tamanho="pequeno"
                variante="fantasma"
                disabled={!catalogo.data}
                onClick={() => setEdicao(painel)}
              >
                Editar
              </Botao>
              {aApagar === painel.id ? (
                <>
                  <Botao
                    tamanho="pequeno"
                    disabled={apagar.isPending}
                    onClick={() => apagar.mutate(painel.id, { onSettled: () => setAApagar(null) })}
                  >
                    Confirmar exclusão
                  </Botao>
                  <Botao tamanho="pequeno" variante="fantasma" onClick={() => setAApagar(null)}>
                    Manter
                  </Botao>
                </>
              ) : (
                <Botao tamanho="pequeno" variante="fantasma" onClick={() => setAApagar(painel.id)}>
                  Apagar
                </Botao>
              )}
            </div>
          </Cartao>
        ))}
      </div>
    </>
  );
}
